/**
 * Rename a project or a pipeline.
 *
 * One modal for both, because the operation is the same shape and the rules
 * that differ (a promoted pipeline cannot be renamed; a name must be unique
 * among its siblings) are enforced server-side. The dialog does not try to
 * predict them — it shows what the server said, which is the only version of
 * those rules that is always current.
 */

import { useEffect, useState } from "react";
import { Button, Group, Modal, Stack, TextInput, Textarea } from "@mantine/core";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";

interface Props {
  opened: boolean;
  onClose: () => void;
  /** What is being renamed, for the title and the toast. */
  what: "project" | "pipeline" | "collection";
  name: string;
  description?: string | null;
  /** Performs the rename; resolves with the new name. */
  onSubmit: (name: string, description: string | null) => Promise<void>;
}

export function RenameModal({ opened, onClose, what, name, description, onSubmit }: Props) {
  const [value, setValue] = useState(name);
  const [desc, setDesc] = useState(description ?? "");
  const [saving, setSaving] = useState(false);

  // Reopening after a cancel should show what is currently saved, not what was
  // typed and abandoned last time.
  useEffect(() => {
    if (opened) {
      setValue(name);
      setDesc(description ?? "");
    }
  }, [opened, name, description]);

  const submit = async () => {
    const next = value.trim();
    if (!next) return;
    setSaving(true);
    try {
      await onSubmit(next, desc.trim() || null);
      toastSuccess("Renamed", `Now called “${next}”.`);
      onClose();
    } catch (err) {
      toastApiError("Could not rename", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={`Rename ${what}`} centered>
      <Stack gap="sm">
        <TextInput
          label="Name"
          data-autofocus
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
        />
        <Textarea
          label="Description"
          autosize
          minRows={2}
          value={desc}
          onChange={(e) => setDesc(e.currentTarget.value)}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving} disabled={!value.trim()}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
